import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, NavItem, NavLink } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { FaUserCircle } from 'react-icons/fa';

import Avatar from '../Avatar';
import * as actions from '../../store/modules/auth/actions';
import axios from '../../services/axios';
import { useNotification } from '../Notification/NotificationContext';
import ErrorProcessor from '../ErrorProcessor';

// Definir o URL base da API para concatenar o caminho da foto
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Ajuste conforme necessário

export default function UserMenu() {
  // eslint-disable-next-line no-unused-vars
  const notification = useNotification();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userName, setUserName] = useState('');
  const [userLastname, setUserLastname] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userPhoto, setUserPhoto] = useState('');
  const userId = useSelector((state) => state.auth.user.id);
  const authToken = useSelector((state) => state.auth.token);
  const [apiErrors, setApiErrors] = useState({});

  const handleLogout = useCallback(() => {
    dispatch(actions.loginFailure());
    navigate('/login');
  }, [dispatch, navigate]);

  const handleClickLink = (e) => {
    e.preventDefault();
    const route = e.target.getAttribute('data-route');

    if (route) {
      navigate(route);
    }
  };

  useEffect(() => {
    if (!userId) return;
    async function getData() {
      try {
        const { data } = await axios.get(`users/${userId}`);

        // Obtém o caminho da foto recebido da API
        const photoPath = get(data, 'UserProfile.photo', '');

        if (!photoPath) {
          setUserPhoto('');
        } else {
          // Normaliza o caminho
          const normalizedPath = String(photoPath).replace(/\\/g, '/');
          let finalUrl = '';

          // Se já for uma URL absoluta
          if (/^https?:\/\//i.test(normalizedPath)) {
            finalUrl = normalizedPath;
          } else {
            // Converte /public/ ou /api/public/ para /download/ (sem /api/ pois API_BASE_URL já tem)
            let convertedPath = normalizedPath;
            if (convertedPath.startsWith('/api/public/')) {
              // Remove /api/public/ e adiciona /download/
              convertedPath = convertedPath.replace(
                '/api/public/',
                '/download/'
              );
            } else if (convertedPath.startsWith('/api/download/')) {
              // Remove /api/ do início pois API_BASE_URL já tem
              convertedPath = convertedPath.replace(
                '/api/download/',
                '/download/'
              );
            } else if (convertedPath.startsWith('/public/')) {
              // Remove /public/ e adiciona /download/
              convertedPath = convertedPath.replace('/public/', '/download/');
            } else if (!convertedPath.startsWith('/')) {
              // Se não começar com /, adiciona /download/
              convertedPath = `/download/${convertedPath}`;
            }

            // Constrói a URL completa (API_BASE_URL já contém /api)
            finalUrl = `${API_BASE_URL}${convertedPath}`;
          }

          // Adiciona o token na query string se disponível
          if (
            authToken &&
            authToken !== false &&
            finalUrl.includes('/download/')
          ) {
            const separator = finalUrl.includes('?') ? '&' : '?';
            finalUrl = `${finalUrl}${separator}token=${encodeURIComponent(authToken)}`;
          }

          setUserPhoto(finalUrl);
        }

        setUserName(
          data.UserProfile && data.UserProfile.name ? data.UserProfile.name : ''
        );
        setUserLastname(
          data.UserProfile && data.UserProfile.lastname
            ? data.UserProfile.lastname
            : ''
        );
        setUserEmail(data.email);
      } catch (err) {
        setApiErrors(err);
      }
    }

    getData();
  }, [userId, authToken]);

  return (
    <Dropdown as={NavItem} className="ms-auto" align="end">
      <ErrorProcessor error={apiErrors} />
      <Dropdown.Toggle as={NavLink} className="pe-0" id="userInfo">
        {userPhoto ? (
          <Avatar image={userPhoto} alt="Foto de perfil" border priority />
        ) : (
          <FaUserCircle size={48} />
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu
        className="dropdown-menu-animated"
        aria-labelledby="userInfo"
        data-bs-popper="none"
      >
        <Dropdown.Header className="text-gray-700">
          <h6 className="text-uppercase font-weight-bold">
            {userName} {userLastname}
          </h6>
          <small>{userEmail.split(' ')[0]}</small>
        </Dropdown.Header>
        <Dropdown.Divider />
        <Dropdown.Item
          data-route={`/profile/${userId || 0}`}
          onClick={handleClickLink}
        >
          Perfil
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item onClick={handleLogout}>Sair</Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
